import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ENO1DispatchData } from './useENO1DispatchData';

// Interface for creating a new sample
export interface CreateENO1SampleRequest extends Partial<ENO1DispatchData> {
  fileId: number; // Required field for new sample creation
}

/**
 * Hook for adding a new eNO1 sample
 * 
 * @returns Mutation for creating a new eNO1 sample
 */
export const useAddENO1Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newSample: CreateENO1SampleRequest) => {
       // Transform dataSetId to fileId for the API request
      const { dataSetId, ...rest } = newSample as any;
      const payload = {
        ...rest,
        dataSetId: dataSetId, // Map dataSetId to fileId for the API
      };
      
      const response = await axios.post('/data-sample/eno1', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['eno1DispatchData', variables.fileId] 
      });
    },
  });
};

/**
 * Hook for updating an existing eNO1 sample
 * 
 * @returns Mutation for updating an eNO1 sample
 */
export const useUpdateENO1Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updatedSample: ENO1DispatchData) => {
      // Transform dataSetId to fileId for the API request
      const { dataSetId, ...rest } = updatedSample as any;
      
      // Ensure eno1FileId is set, using fileId as a fallback
      const eno1FileId = rest.eno1FileId || rest.fileId;
      
      const payload = {
        ...rest,
        dataSetId: dataSetId, // Ensure eno1FileId is always set
      };
      
      const response = await axios.put('/data-sample/eno1', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['eno1DispatchData'] 
      });
    },
  });
};

/**
 * Hook for deleting an eNO1 sample or amendment
 * 
 * @returns Mutation for deleting an eNO1 sample
 */
export const useDeleteENO1Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sampleId, fileId }: { sampleId: number; fileId: number }) => {
      // API expects query parameters: sampleId and dataSetId
      const response = await axios.delete(`/data-sample/eno1`, {
        params: {
          sampleId,
          dataSetId: fileId,
        },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['eno1DispatchData', variables.fileId] 
      });
    },
  });
}; 